import React, { ReactNode, useMemo } from 'react';
import { cn } from '@/lib/utils';

/**
 * If your project exposes this type from a hook, import it directly.
 * We keep it structural here to avoid coupling.
 */
type CentralizedMobileDetection = {
  isMobile: boolean;
  platform?: 'ios' | 'android' | 'desktop';
};

/**
 * StandardDialogContent
 * ----------------------------------------------------------------------------
 * Responsibilities:
 * - Provide the scrollable body area for any StandardDialog.
 * - Respect iOS/Android safe-area insets (top & bottom).
 * - Ensure a visible scrollbar for long content (overflow-y-auto).
 * - Play nicely inside a flex column parent with header/footer above/below.
 *
 * Notes:
 * - We intentionally apply *padding* for safe areas here so content never hides
 *   under the notch (top) or home indicator (bottom).
 * - If your header/footer already accounts for safe-area at their edges, this
 *   double-protection is harmless since padding is minimal (env() resolves to 0
 *   outside mobile safe areas).
 */

interface StandardDialogContentProps {
  children: ReactNode;
  /** Additional classes for the outer content container */
  className?: string;
  /** Enable vertical scrolling (default: true) */
  scrollable?: boolean;
  /** Expansion state; included for potential future style hooks */
  isExpanded?: boolean;
  /** Optional detection object from a centralized mobile detection hook */
  detection?: CentralizedMobileDetection;
  /** Optional aria-label override for accessibility */
  ariaLabel?: string;
  /** Optional role override (default: 'main') */
  roleAttr?: string;
  /** If you want to hide horizontal overflow explicitly */
  hideOverflowX?: boolean;
}

const StandardDialogContent: React.FC<StandardDialogContentProps> = ({
  children,
  className,
  scrollable = true,
  isExpanded = false,
  detection,
  ariaLabel = 'Dialog content',
  roleAttr = 'main',
  hideOverflowX = true,
}) => {
  // Decide whether to apply safe-area padding.
  // We default to applying it on mobile; if detection is missing, we still add
  // it because env(safe-area-inset-*) gracefully resolves to 0 on non-iOS.
  const applySafeArea = useMemo(() => {
    if (!detection) return true;
    return detection.isMobile !== false;
  }, [detection]);

  const safeAreaStyle: React.CSSProperties = applySafeArea
    ? {
        // Top padding so content never scrolls under the notch / status area
        paddingTop: 'env(safe-area-inset-top, 0px)',
        // Bottom padding so CTA/footer content is clear of the home indicator  
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }
    : {};

  return (
    <div
      role={roleAttr}
      aria-label={ariaLabel}
      className={cn(
        // Layout: occupy remaining height in a flex column wrapper
        'flex-1 flex flex-col relative min-h-0',
        // Vertical scrolling container
        scrollable ? 'overflow-y-auto' : 'overflow-y-hidden',
        // Optional horizontal overflow clamp
        hideOverflowX ? 'overflow-x-hidden' : '',
        // Allow consumers to add spacing/skins
        className
      )}
      style={{
        // Smooth anchor/scroll behavior for in-content links
        scrollBehavior: 'smooth',
        // Make sure padding contributes to height calculations properly
        boxSizing: 'border-box',
        // Safe-area paddings
        ...safeAreaStyle,
      }}
    >
      {/* 
        Tip: If your design wants a consistent inner horizontal padding,
        set it at the usage site so different dialogs can choose different
        gutters (e.g., px-4).
      */}
      {children}
    </div>
  );
};

export default StandardDialogContent;
export { StandardDialogContent };
